import { useEffect, useMemo, useState } from 'react';
import Spinner from '../components/Spinner';
import { devGetUsageSummary, devListUsageEvents, UsageSummary } from '../api/dev';

type Ev = { ts: string; service: string; apiPath?: string; apiKeyId?: string; units?: number; detail?: any };

export default function DevApiCreditUsage() {
  const [from, setFrom] = useState<string>(isoDate(new Date(Date.now() - 7*24*3600*1000)));
  const [to, setTo] = useState<string>(isoDate(new Date()));
  const [service, setService] = useState<string>('');
  const [keyId, setKeyId] = useState<string>('');
  const [items, setItems] = useState<Ev[]>([]);
  const [summary, setSummary] = useState<UsageSummary | null>(null);
  const [detail, setDetail] = useState<any | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const filtered = useMemo(() => items, [items]);
  const costs = useMemo(() => filtered.map((ev) => {
    const n = Number((ev as any).units ?? (ev as any)?.detail?.units ?? 1);
    return Math.max(1, Math.floor(Number.isFinite(n) ? n : 1));
  }), [filtered]);
  const prefix = useMemo(() => {
    const out: number[] = new Array(costs.length).fill(0);
    let acc = 0;
    for (let i = 0; i < costs.length; i++) {
      out[i] = acc; // sum of prior (newer) events
      acc += costs[i];
    }
    return out;
  }, [costs]);

  async function search() {
    setLoading(true); setError(''); setItems([]);
    try {
      const [sum, out] = await Promise.all([
        devGetUsageSummary().catch(() => null),
        devListUsageEvents({ dateFrom: from, dateTo: to, service: service || undefined, keyId: keyId || undefined, limit: 2000 }),
      ]);
      const list = (out.items || []).slice().sort((a, b) => new Date(b.ts).getTime() - new Date(a.ts).getTime());
      setItems(list);
      setSummary(sum);
    } catch (e: any) { setError(e?.message || String(e)); }
    finally { setLoading(false); }
  }
  useEffect(() => { search(); }, []);

  function exportCsv() {
    const rows = [['timestamp','service','apiPath','apiKeyId']].concat(filtered.map(ev => [ev.ts, ev.service, ev.apiPath||'', ev.apiKeyId||'']));
    const csv = rows.map(r => r.map(v => v.includes(',') ? '"'+v.replace(/"/g,'""')+'"' : v).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `api-usage-${from}-${to}.csv`;
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(a.href), 2000);
  }

  return (
    <div>
      <h2>API Credit Usage</h2>
      <div className="card" style={{ marginBottom: 12 }}>
        <div className="row" style={{ gap: 8, alignItems: 'flex-end' }}>
          <label>From
            <input className="txt" type="datetime-local" value={from} onChange={e => setFrom(e.target.value)} />
          </label>
          <label>To
            <input className="txt" type="datetime-local" value={to} onChange={e => setTo(e.target.value)} />
          </label>
          <label>Service
            <input className="txt" placeholder="e.g. current" value={service} onChange={e => setService(e.target.value)} />
          </label>
          <label>Key ID
            <input className="txt" placeholder="optional" value={keyId} onChange={e => setKeyId(e.target.value)} />
          </label>
          <button className="btn" onClick={search} disabled={loading}>Search</button>
          <button className="btn secondary" onClick={exportCsv} disabled={filtered.length===0}>Export CSV</button>
        </div>
      </div>
      {loading && <Spinner label="Loading..." />}
      {error && <div style={{ color:'crimson', marginBottom: 8 }}>Error: {error}</div>}
      <div className="card">
        <table className="tbl">
          <thead><tr><th>Timestamp</th><th>Service</th><th>API Path</th><th>Key ID</th><th>Units</th><th>Remaining</th></tr></thead>
          <tbody>
            {filtered.length === 0 && <tr><td colSpan={6}><span className="muted">No results</span></td></tr>}
            {filtered.map((ev, i) => {
              const cost = costs[i] ?? 1;
              const remBase = summary?.remainingMonth;
              const rem = typeof remBase === 'number' ? remBase + (prefix[i] ?? 0) : undefined;
              const keyDisp = (ev as any)?.apiKeyId || (ev as any)?.detail?.apiKeyId || '';
              return (
                <tr key={i} onClick={() => setDetail(ev)} style={{ cursor: 'pointer' }}>
                  <td className="muted">{new Date(ev.ts).toLocaleString()}</td>
                  <td>{labelForService(ev.service)}</td>
                  <td className="muted">{ev.apiPath || ''}</td>
                  <td className="muted">{keyDisp}</td>
                  <td>{cost}</td>
                  <td>{rem !== undefined ? rem : '-'}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {detail && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'grid', placeItems: 'center', zIndex: 100 }} onClick={() => setDetail(null)}>
          <div className="card" style={{ width: '90vw', maxWidth: 900 }} onClick={e => e.stopPropagation()}>
            <h3 style={{ marginTop: 0 }}>Usage Event Details</h3>
            <div className="muted" style={{ marginBottom: 8 }}>
              {new Date(detail.ts).toLocaleString()} &bull; {labelForService(detail.service)}
            </div>
            <pre style={{ maxHeight: 420, overflow: 'auto', background: '#0b1220', padding: 12, borderRadius: 8 }}>
{JSON.stringify(detail, null, 2)}
            </pre>
            <div className="row" style={{ justifyContent: 'flex-end', gap: 8 }}>
              <button className="btn" onClick={() => setDetail(null)}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function isoDate(d: Date): string {
  const pad = (n: number) => n.toString().padStart(2, '0');
  const yyyy = d.getFullYear();
  const mm = pad(d.getMonth() + 1);
  const dd = pad(d.getDate());
  const hh = pad(d.getHours());
  const mi = pad(d.getMinutes());
  return `${yyyy}-${mm}-${dd}T${hh}:${mi}`;
}

function labelForService(id: string): string {
  switch (id) {
    case 'api-credit': return 'API Credit Service';
    case 'current': return 'Current API';
    case 'forecast-hourly': return 'Forecast Hourly API';
    case 'forecast-daily': return 'Forecast Daily';
    case 'history': return 'History API';
    case 'astronomical': return 'Astronomical API';
    case 'bulk-current': return 'Bulk Current';
    case 'bulk-forecast-hourly': return 'Bulk Forecast Hourly';
    case 'forecast-push': return 'Forecast Push Notifications';
    default: return id;
  }
}


